import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatSelectModule,
} from '@angular/material/select';
import {
  NavigationEnd,
  Route,
  Router,
  Routes,
} from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { SidebarModule } from 'primeng/sidebar';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Subject,
  takeUntil,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { isCourtBasedSportEnv } from '../../shared/sport-terminology';
import { CommonServiceService } from '../../utilities/services/common-service/common-service.service';
import { AuthServiceService } from '../../utilities/services/auth-service/auth-service.service';
import { WebHandoffCodeResponse } from '../../models/auth/web-handoff.model';
import { EncryptDecryptService } from '../../utilities/services/EncrptyDecrypt/encrypt-decrypt.service';
import { PointLeaderboardService } from '../../utilities/services/point-leaderboard/point-leaderboard.service';
import { RoleServiceService } from '../../utilities/services/role-service/role-service.service';
import {
  ThemeServiceService,
  ThemeType,
} from '../../utilities/services/theme-service.service';
import { UserProfileStoreService } from '../../utilities/services/userProfile/user-profile-store.service';
import { hardLogoutAndRedirectHome } from '../../utilities/auth-session.util';
import { LanguageService } from '../../utilities/services/language.service';
import { resolveUiLanguage, persistManualUiLanguage } from '../../utilities/utils/client-language.util';
import { SubLicenseManagementService } from '../sub-licenses/sub-license-management/sub-license-management.service';
import { UiConfigService } from '../../utilities/services/ui-config/ui-config.service';
import { NotificationManagerComponent } from './notification-manager/notification-manager.component';
import { HeaderNavGroupComponent } from './header-nav-group/header-nav-group.component';
import { HeaderNavGroupItem } from './header-nav-group/header-nav-group.model';
import { HeaderNavIconComponent } from './header-nav-group/header-nav-icon.component';
import { HttpRes } from '../../models/common/http.model';
import { SkeletonLoaderComponent } from '../shared/skeleton-loader/skeleton-loader.component';
import {
  GlobalSearchResult,
  GlobalSearchService,
} from '../../utilities/services/global-search/global-search.service';
import { UserMenuService } from '../../utilities/services/user-menu/user-menu.service';

interface ProfileMenuItem {
  name: string;
  id: number;
}

interface ProfileSelection {
  value: number | null;
  navigationType?: string;
}

interface EligibleRolesApiBody {
  data: EligibleRoleRow[];
}

interface EligibleRoleRow {
  role_id: number;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SkeletonLoaderComponent,
    HttpClientModule,
    SidebarModule,
    MatSelectModule,
    OverlayPanelModule,
    NotificationManagerComponent,
    HeaderNavGroupComponent,
    HeaderNavIconComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  // UserProfileStoreService must stay root-provided (not listed here): a
  // component-level provider would create a second instance and break the
  // shared avatar state.
  providers: [RoleServiceService],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('profileOverlay') profileOverlay!: OverlayPanel;
  @ViewChild('languageOverlay') languageOverlay!: OverlayPanel;
  @ViewChild('themeOverlay') themeOverlay!: OverlayPanel;
  @Output() languageChange = new EventEmitter<string>();

  currentLang = resolveUiLanguage();
  currentTheme: ThemeType = 'default-custom-theme';
  readonly availableThemes: { value: ThemeType; label: string }[] = [
    { value: 'default-custom-theme', label: 'Default' },
    { value: 'switz-custom-theme', label: 'Switz' },
    { value: 'rfetm-custom-theme', label: 'RFETM' },
    { value: 'nbtf-custom-theme', label: 'Nbtf' },
    { value: 'ptta-custom-theme', label: 'PTTA' },
    { value: 'ttnz-custom-theme', label: 'NTZS' },
    { value: 'sbtf-custom-theme', label: 'SBTF' },
  ];
  sport_id: number = environment.sport_id;
  availableLanguages: Array<{ code: string; name: string }> = [];
  private readonly leaderboardService = inject(PointLeaderboardService);
  private readonly uiConfig = inject(UiConfigService);
  /** Opt-in: missing key = hidden. Set `show_notify_users: true` in tenant uiConfig to show. */
  readonly showNotifyUsers = this.uiConfig.getValue('show_notify_users', false);
  user_data: unknown = null;
  selected_tab: number | null = null;
  show_profile_name: boolean = true;
  profile_list: ProfileMenuItem[] = [
    { name: 'my_profile', id: 1 },
    { name: 'logout', id: 3 },
  ];
  new_profile_image: string = '';
  /** Shared avatar state (root singleton) — template reads profileStore.profilePic(). */
  readonly profileStore = inject(UserProfileStoreService);
  /** Falls back to the generic user icon when the photo URL fails to load. */
  avatarError = false;
  // Reset the broken-image fallback whenever the store publishes a new URL.
  private readonly resetAvatarErrorOnChange = effect(() => {
    this.profileStore.profilePic();
    this.avatarError = false;
  });
  private readonly destroy$ = new Subject<void>();
  _currentRoute = '';
  innerWidth = 0;
  show_sidebar: boolean = false;

  /** Bottom sheet: drag tab line down to dismiss; list rows use normal clicks. */
  sidebarDragTranslateY = 0;
  private sidebarPanPointerId: number | null = null;
  private sidebarPanStartY = 0;
  private readonly sidebarPanDismissPx = 96;
  all_eligibleRole: EligibleRoleRow[] = [];
  Open_dialog: boolean = false;
  is_dark: boolean = false;
  openDropDownMenu: string | null = null;
  logo: string = environment.logo_light;
  logo_dark: string = environment.logo_dark;
  logo_light: string = environment.logo_light;
  is_membership_available: boolean = environment.is_membership_available;
  is_invoice_history: boolean = environment.is_invoice_history;
  show_broadcasts: boolean = environment.show_broadcasts;
  show_live: boolean = environment.show_live;
  show_player_dashboard: boolean = environment.show_player_dashboad;
  show_loan_and_transfer: boolean = environment.show_loan_and_transfer;
  useRanking: boolean = environment.use_ranking;
  useRating: boolean = environment.use_rating;
  showSubLicense: boolean = environment.show_sub_license;
  showLicenseTable: boolean = (environment as any).show_license_table ?? false;
  admin_url: string = environment.adminUrl;

  /** Icon-only tab compaction when visible tab count exceeds this. Revert: set to 6. */
  readonly navTabsCompactThreshold = 7;

  readonly watchCentreNavItems = computed(() => {
    const items: HeaderNavGroupItem[] = [];
    if (this.show_broadcasts) {
      items.push({
        id: 'broadcasts',
        labelKey: 'video_collection',
        iconId: 'video-collection',
        route: 'broadcasts',
        url: '/broadcasts/4',
      });
    }
    if (this.show_live) {
      items.push({
        id: 'live',
        labelKey: 'live',
        iconId: 'live',
        route: 'live',
        url: '/live',
      });
    }
    return items;
  });

  readonly showWatchCentreGroup = computed(
    () => this.watchCentreNavItems().length >= 2,
  );

  readonly watchCentreTabCount = computed(() => {
    const count = this.watchCentreNavItems().length;
    if (count === 0) {
      return 0;
    }
    return count >= 2 ? 1 : count;
  });

  readonly membershipsNavItems = computed(() => {
    const items: HeaderNavGroupItem[] = [];
    if (this.is_membership_available) {
      items.push({
        id: 'membership',
        labelKey: 'license',
        iconId: 'license',
        route: 'membership',
        url: '/membership',
        titlecase: true,
      });
    }
    if (this.showSubLicense) {
      items.push({
        id: 'sub-licenses',
        labelKey: 'sub_license',
        iconId: 'members',
        route: 'sub-licenses',
        url: '/sub-licenses',
      });
    }
    return items;
  });

  readonly showMembershipsGroup = computed(
    () => this.membershipsNavItems().length >= 2,
  );

  readonly membershipsTabCount = computed(() => {
    const count = this.membershipsNavItems().length;
    if (count === 0) {
      return 0;
    }
    return count >= 2 ? 1 : count;
  });

  /** Count of visible desktop nav tabs (mirrors the *ngIf conditions on the
   *  `.link-row` items below) — drives icon-only compaction once there are
   *  too many tabs for full labels to fit without overlapping the search icon. */
  get visibleNavTabCount(): number {
    const homeAndEvents = 2;
    return (
      homeAndEvents +
      (this.is_login && this.isPlayer && this.show_player_dashboard ? 1 : 0) +
      this.watchCentreTabCount() +
      (this.useRanking ? 1 : 0) +
      this.membershipsTabCount() +
      (this.useRating ? 1 : 0)
    );
  }

  onNavGroupItemNavigate(item: HeaderNavGroupItem): void {
    if (item.route === 'membership') {
      this.navigateToMembership();
      return;
    }
    this.navigateToRegistration(item.url);
  }

  @HostListener('window:resize')
  onResize() {
    if (globalThis.window !== undefined) {
      this.innerWidth = globalThis.window.innerWidth;
      this.syncMobileSearchBodyScrollLock();
    }
  }
  is_login: boolean = false;
  is_admin: boolean = false;
  private resolveTenantName(): string {
    if (environment.tenant_Name !== 'switz' && environment.tenant_Name !== 'test_switz') {
      return environment.tenant_Name;
    }
    return 'switz';
  }

  tenant_name = this.resolveTenantName();

  // Search properties
  searchQuery: string = '';
  isSearchFocused: boolean = false;
  isSearchExpanded: boolean = false;
  isSearchLoading: boolean = false;
  searchResults: any[] = [];
  private readonly searchSubject = new Subject<string>();

  /** Body class + scroll restore when `.mobile-search-overlay` blocks page scroll */
  private readonly mobileSearchBodyScrollLockClass =
    'mobile-search-scroll-lock';
  private mobileSearchLockedScrollY = 0;

  isThemeSwitchAvailable = false;

  // Voice search properties
  isListening: boolean = false;
  recognition: any = null;
  isVoiceSearchSupported: boolean = false;

  // Cache for prefetched modules to avoid duplicate prefetch calls
  private readonly prefetchedModules = new Set<string>();
  constructor(
    private readonly themeService: ThemeServiceService,
    private readonly router: Router,
    private readonly encryptDecryptService: EncryptDecryptService,
    private readonly commonService: CommonServiceService,
    private readonly roleService: RoleServiceService,
    private readonly subLicenseManagementService: SubLicenseManagementService,
    private readonly globalSearchService: GlobalSearchService,
    private readonly userMenuService: UserMenuService,
    private readonly languageService: LanguageService,
    private readonly authService: AuthServiceService
  ) {
    if (globalThis.window !== undefined) {
      this.innerWidth = globalThis.window.innerWidth;
    }
    this.isThemeSwitchAvailable =
      environment.tenant_Name === 'dev' ||
      environment.tenant_Name === 'staging' ||
      environment.tenant_Name === 'preprod';
  }

  private syncSelectedProfileTab(): void {
    if (this.router.url.split('/')[2] == 'my-profile') {
      this.selected_tab = this.profile_list[0].id;
    } else if (
      this.router.url.split('/')[1] == 'order-history' &&
      this.router.url.split('/').length === 2
    ) {
      // Keep selected_tab when on order-history root.
    } else {
      this.selected_tab = null;
    }
    this.show_profile_name = this.selected_tab == null;
  }

  private getTopLevelRoute(url: string): string {
    // Normalize route: ignore query params and fragments (e.g. /home?x=1#y -> home)
    const tree = this.router.parseUrl(url || '');
    const primary = tree.root.children['primary'];
    return primary?.segments?.[0]?.path ?? '';
  }

  ngOnInit() {
    // Initialize available languages based on sport_id
    this.initializeLanguages();

    // Subscribe to theme changes
    this.themeService.isDark$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDark: boolean) => {
        this.is_dark = isDark;
      });

    // Subscribe to current theme changes
    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme: ThemeType) => {
        this.currentTheme = theme;
      });

    // Initialize theme values
    this.is_dark = this.themeService.getTheme();
    this.currentTheme = this.themeService.getCurrentTheme();


    this.commonService.is_login_success
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => {
        this.is_login = res;
        if (res) {
          queueMicrotask(() => this.checkRole());
          // BehaviorSubject replays on load, so a logged-in refresh also lands here.
          this.profileStore.loadProfilePic();
        } else {
          this.isPlayer = false;
          this.profileStore.clear();
        }
      });
    this.checkRole();
    const is_admin = JSON.parse(
      this.encryptDecryptService.decryptUsingAES256(
        localStorage.getItem('role_id')
      )
    );

    if (is_admin == 1) {
      this.is_admin = true;
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.syncSelectedProfileTab();
        // Use urlAfterRedirects so the selected tab matches redirects on reload
        this._currentRoute = this.getTopLevelRoute(
          event.urlAfterRedirects || event.url
        );
        if (this.is_login) {
          this.checkRole();
        }
      });
    this.syncSelectedProfileTab();
    // Initialize _currentRoute with the current URL
    this._currentRoute = this.getTopLevelRoute(this.router.url);

    if (
      globalThis.window?.localStorage
    ) {
      this.user_data = JSON.parse(
        this.encryptDecryptService.decryptUsingAES256(
          localStorage.getItem('_unSavedEventDetails')
        )
      );
    }
    this.initSearchSubscription();
    this.initVoiceRecognition();
  }

  openProfileOverlay(event: Event) {
    if (this.languageOverlay) {
      this.languageOverlay.hide();
    }
    if (this.themeOverlay) {
      this.themeOverlay.hide();
    }
    this.profileOverlay.toggle(event);
  }

  /** Close profile overlay / mobile sidebar before route change. */
  private dismissHeaderOverlays(): void {
    this.openDropDownMenu = '';
    this.show_sidebar = false;
    if (this.profileOverlay) {
      this.profileOverlay.hide();
    }
  }

  openLanguageOverlay(event?: Event) {
    if (this.profileOverlay) {
      this.profileOverlay.hide();
    }
    if (this.themeOverlay) {
      this.themeOverlay.hide();
    }
    this.languageOverlay.toggle(event);
  }

  openThemeOverlay(event?: Event) {
    if (this.profileOverlay) {
      this.profileOverlay.hide();
    }
    if (this.languageOverlay) {
      this.languageOverlay.hide();
    }
    this.themeOverlay.toggle(event);
  }

  selectTheme(theme: ThemeType) {
    this.themeService.setTheme(theme);
    this.currentTheme = theme;
    if (this.themeOverlay) {
      this.themeOverlay.hide();
    }
  }

  onSearchFocus() {
    this.isSearchFocused = true;
  }

  toggleSearch() {
    this.isSearchExpanded = !this.isSearchExpanded;
    if (this.isSearchExpanded) {
      // Focus the input after it appears
      setTimeout(() => {
        const input = document.querySelector(
          '.search-input'
        ) as HTMLInputElement;
        if (input) input.focus();

        const mobileInput = document.querySelector(
          '.mobile-search-input'
        ) as HTMLInputElement;
        if (mobileInput) mobileInput.focus();
      }, 50);
    } else {
      this.searchQuery = '';
      this.searchResults = [];
    }
    this.syncMobileSearchBodyScrollLock();
  }

  clearSearch(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.searchQuery = '';
    this.searchResults = [];
    // Keep focus on input after clearing
    setTimeout(() => {
      const input = document.querySelector('.search-input') as HTMLInputElement;
      if (input) input.focus();
    }, 10);
  }

  onSearchBlur() {
    // Delay to allow click on results
    setTimeout(() => {
      this.isSearchFocused = false;
      // Collapse if empty
      if (this.searchQuery.length === 0) {
        this.isSearchExpanded = false;
      }
      this.syncMobileSearchBodyScrollLock();
    }, 200);
  }

  private mobileSearchUsesFullScreenOverlay(): boolean {
    if (!this.isSearchExpanded) {
      return false;
    }
    const w =
      globalThis.window === undefined ? this.innerWidth : globalThis.window.innerWidth;
    return typeof w === 'number' && w <= 1024;
  }

  /** Keep in sync with `mobile-search-overlay` (*ngIf `innerWidth <= 1024 && isSearchExpanded`). */
  private syncMobileSearchBodyScrollLock(): void {
    if (document === undefined || globalThis.window === undefined) {
      return;
    }
    const body = document.body;
    const isLocked = body.classList.contains(this.mobileSearchBodyScrollLockClass);
    const shouldLock = this.mobileSearchUsesFullScreenOverlay();

    if (shouldLock) {
      if (!isLocked) {
        this.mobileSearchLockedScrollY = globalThis.window.scrollY;
        body.style.top = `-${this.mobileSearchLockedScrollY}px`;
        body.classList.add(this.mobileSearchBodyScrollLockClass);
      }
      return;
    }

    if (isLocked) {
      this.releaseMobileSearchBodyScrollLock();
    }
  }

  private releaseMobileSearchBodyScrollLock(): void {
    if (document === undefined || globalThis.window === undefined) {
      return;
    }
    const body = document.body;
    if (!body.classList.contains(this.mobileSearchBodyScrollLockClass)) {
      return;
    }
    body.classList.remove(this.mobileSearchBodyScrollLockClass);
    body.style.top = '';
    globalThis.window.scrollTo(0, this.mobileSearchLockedScrollY);
  }

  // onSearchInput(event: Event) {
  //   const query = (event.target as HTMLInputElement).value;
  //   if (query.length >= 3) {
  //     this.searchSubject.next(query);
  //   } else {
  //     this.searchResults = [];
  //     this.isSearchLoading = false;
  //   }
  // }
  onSearchInput(event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchSubject.next(query);
    if (query.length < 3) {
      this.searchResults = [];
      this.isSearchLoading = false;
    }
  }

  private initSearchSubscription() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query: string) => {
        this.performSearch(query);
      });
  }

  /**
   * Runs a global search and mirrors the results into this component's state.
   *
   * Query handling, event de-duplication and result shaping live in
   * {@link GlobalSearchService} so the sidebar rail (template 1) and this
   * header (template 0) behave identically.
   *
   * @param query Raw user input.
   */
  private performSearch(query: string) {
    if (!this.globalSearchService.isSearchable(query)) {
      this.searchResults = [];
      return;
    }

    this.isSearchLoading = true;
    this.globalSearchService
      .search(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results: GlobalSearchResult[]) => {
          this.isSearchLoading = false;
          this.searchResults = results;
        },
        error: (error: unknown) => {
          this.isSearchLoading = false;
          this.searchResults = [];
          console.error('Search error:', error);
        },
      });
  }

  /**
   * Opens a search hit.
   *
   * Routing rules live in {@link GlobalSearchService} so both layouts resolve
   * events, broadcasts and live/recorded streams identically; only the local
   * search UI teardown stays here.
   *
   * @param result Result the user activated.
   */
  onResultClick(result: GlobalSearchResult) {
    this.globalSearchService.openResult(result);

    this.searchQuery = '';
    this.searchResults = [];
    this.isSearchFocused = false;
    this.isSearchExpanded = false;
    this.syncMobileSearchBodyScrollLock();
  }

  // getSystemSettings() method removed - now using environment configuration
  // getSystemSettings() {
  //   this.leaderboardService.getSystemSettings().subscribe((response: any) => {

  //     this.useRanking = response.use_ranking;
  //     this.useRating = response.use_rating;
  //   });
  // }

  /**
   * Handles a profile-menu selection.
   *
   * Each destination is delegated to {@link UserMenuService} so the sidebar
   * rail performs the identical action; only header-local UI state is set here.
   *
   * @param data Selected menu entry.
   */
  selectProfile(data: ProfileSelection) {
    const { navigationType } = data;

    if (navigationType === 'auto') return;

    this.show_profile_name = data.value == null;
    if (data.value == 1) {
      this.navigateToUserProfile();
    } else if (data.value == 2) {
      this.router.navigate(['order-history/']);
      this.eligibleRoles();
    } else if (data.value == 3) {
      this.clearSessionAndNavigateHome();
    }
  }

  private navigateToUserProfile(): void {
    const profileRoute =
      this.isLoggedInUserPlayer() && this.show_player_dashboard
        ? 'my-profile-dashboard'
        : 'my-space/my-profile/';
    this.router.navigate([profileRoute]);
  }

  private isLoggedInUserPlayer(): boolean {
    if (
      globalThis.window === undefined ||
      globalThis.localStorage === undefined
    ) {
      return false;
    }
    try {
      const roleId = JSON.parse(
        this.encryptDecryptService.decryptUsingAES256(
          localStorage.getItem('role_id'),
        ),
      );
      const playerRoleId = localStorage.getItem('player_role_id');
      return (
        !!roleId &&
        !!playerRoleId &&
        roleId.toString() === playerRoleId.toString()
      );
    } catch (e) {
      console.error('Error checking role', e);
      return false;
    }
  }

  private clearSessionAndNavigateHome(): void {
    this.show_sidebar = false;
    this.commonService.is_login_success.next(false);
    this.profileStore.clear();
    hardLogoutAndRedirectHome();
    this.commonService.login_subject.next(true);
  }


  /**
   * Opens the admin app via a short-lived handoff code (not the raw auth token).
   * @returns void
   */
  navigate_to_web(): void {
    if (globalThis.window === undefined) {
      return;
    }
    this.authService.getWebHandoffCode()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: WebHandoffCodeResponse) => {
          const code = res?.data?.data?.code;
          if (code) {
            const separator = this.admin_url?.includes('?') ? '&' : '?';
            globalThis.window.open(`${this.admin_url}${separator}code=${code}`, '_blank');
          } else {
            this.commonService.toast(res?.data?.msg || 'Could not open the admin app', 'error');
          }
        },
        error: () => {
          this.commonService.toast('Could not open the admin app', 'error');
        }
      });
  }

  navigateToRegistration(url: string) {
    sessionStorage.removeItem('selectedTournamentName');
    sessionStorage.removeItem('selectedTournamentDetails');
    sessionStorage.removeItem('event_id');
    sessionStorage.removeItem('event_type');
    sessionStorage.removeItem('officialType');
    // When navigating to sub-licenses from header, clear stored role so first role is selected
    if (url === '/sub-licenses') {
      this.subLicenseManagementService.clearSelectedRole();
    }
    this._currentRoute = this.getTopLevelRoute(url);

    // Preload the module before navigation for faster loading
    this.preloadModuleForRoute(url);

    this.router.navigate([url]);
    if (this._currentRoute == 'registration') {
      this.eligibleRoles();
      this.eligibleRolesAccomodation();
    }
    this.openDropDownMenu = '';
  }

  /**
   * Prefetch module for a given route to improve navigation performance
   */
  preloadModuleForRoute(url: string): void {
    const routePath = url.split('/')[1];

    // Skip if already prefetched
    if (this.prefetchedModules.has(routePath)) {
      return;
    }

    const config = this.router.config;

    // Find the route configuration
    const findRoute = (routes: Routes): Route | undefined => {
      for (const route of routes) {
        if (route.path === routePath && route.loadChildren) {
          return route;
        }
        if (route.children) {
          const found = findRoute(route.children);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    const routeConfig = findRoute(config);

    if (routeConfig?.loadChildren) {
      // Mark as prefetched
      this.prefetchedModules.add(routePath);

      const loadChildren = routeConfig.loadChildren;
      const loadPromise =
        typeof loadChildren === 'function' ? loadChildren() : loadChildren;

      void Promise.resolve(loadPromise).catch((err: unknown) => {
        // Remove from cache on error so we can retry
        this.prefetchedModules.delete(routePath);
        // Silently handle errors - preloading is optional
        console.debug('Preload failed for route:', routePath, err);
      });
    }
  }

  /**
   * Prefetch module on tab hover for better UX
   */
  onTabHover(url: string): void {
    // Only prefetch if not already on that route
    const currentRoute = this.getTopLevelRoute(this.router.url);
    const targetRoute = this.getTopLevelRoute(url);

    if (currentRoute !== targetRoute) {
      this.preloadModuleForRoute(url);
    }
  }
  navigateToOrderHistory() {
    this.router.navigate(['order-history/membership']);
    this.openDropDownMenu = '';
  }
  navigateToMembership() {
    this.preloadModuleForRoute('/membership');
    this.router.navigate(['membership']);
    this.openDropDownMenu = '';
  }

  toggleDropDownMenuItem(menu: string) {

    if (this.openDropDownMenu === menu) {
      this.openDropDownMenu = '';
    } else {
      this.openDropDownMenu = menu;
    }

  }

  initializeLanguages() {
    // Define all available languages
    const allLanguages = [
      { code: 'en', name: 'English' },
      { code: 'norway', name: 'Norwegian' },
      { code: 'swedan', name: 'Swedish' },
      { code: 'sv', name: 'Slovenian' },
      { code: 'spain', name: 'Spanish' },
      { code: 'cs', name: 'Czech' },
      { code: 'da', name: 'Danish' },
      { code: 'de', name: 'German' },
      { code: 'fr', name: 'French' },
      { code: 'it', name: 'Italian' },
    ];

    // Define limited languages for sport_id 2
    const limitedLanguages = [
      { code: 'en', name: 'English' },
      { code: 'de', name: 'German' },
      { code: 'fr', name: 'French' },
      { code: 'it', name: 'Italian' },
    ];

    if (isCourtBasedSportEnv()) {
      this.availableLanguages = limitedLanguages;

      // Check if current language is not in the limited list
      const isCurrentLangAvailable = limitedLanguages.some(
        (lang) => lang.code === this.currentLang
      );

      // If current language is not available, set to English
      if (!isCurrentLangAvailable) {
        this.selectLanguage('en');
      }
    } else {
      this.availableLanguages = allLanguages;
      const isSbtf =
        environment.tenant_Name === 'sbtf' || environment.tenant_Name === 'sbtf_test';
      if (isSbtf && this.currentLang === 'sv') {
        this.selectLanguage('swedan');
      }
    }
  }

  isLanguageAvailable(langCode: string): boolean {
    return this.availableLanguages.some((lang) => lang.code === langCode);
  }

  selectLanguage(lang: string) {
    // Only allow selection of available languages
    if (!this.isLanguageAvailable(lang)) {
      console.warn(
        `Language ${lang} is not available for sport_id ${this.sport_id}`
      );
      return;
    }

    this.currentLang = lang;
    persistManualUiLanguage(lang);
    this.languageService.setLanguage(lang);
    this.languageChange.emit(lang);

    // Update speech recognition language
    if (this.recognition) {
      this.recognition.lang = this.getRecognitionLanguage();
    }

    if (this.languageOverlay) {
      this.languageOverlay.hide();
    }
  }

  get sidebarPanSheetTransform(): string {
    return `translate3d(0, ${Math.max(0, this.sidebarDragTranslateY)}px, 0)`;
  }

  /** When no pointer is captured, CSS may ease the sheet back to y=0. */
  get sidebarPanSnap(): boolean {
    return this.sidebarPanPointerId === null;
  }

  onNavSidebarVisibleChange(visible: boolean): void {
    if (!visible) this.resetSidebarPanState();
  }

  private resetSidebarPanState(): void {
    this.sidebarDragTranslateY = 0;
    this.sidebarPanPointerId = null;
  }

  /** Tab-line host: single entry for down / move / up / cancel. */
  onSidebarPanHandle(ev: PointerEvent): void {
    if (!this.show_sidebar) return;

    switch (ev.type) {
      case 'pointerdown':
        this.onSidebarPanPointerDown(ev);
        break;
      case 'pointermove':
        this.onSidebarPanPointerMove(ev);
        break;
      case 'pointerup':
      case 'pointercancel':
        this.onSidebarPanPointerEnd(ev);
        break;
    }
  }

  private onSidebarPanPointerDown(ev: PointerEvent): void {
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
    ev.preventDefault();
    this.sidebarPanPointerId = ev.pointerId;
    this.sidebarPanStartY = ev.clientY;
    try {
      (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  }

  private onSidebarPanPointerMove(ev: PointerEvent): void {
    if (this.sidebarPanPointerId !== ev.pointerId) return;
    this.sidebarDragTranslateY = Math.max(0, ev.clientY - this.sidebarPanStartY);
  }

  private onSidebarPanPointerEnd(ev: PointerEvent): void {
    if (this.sidebarPanPointerId !== ev.pointerId) return;
    const el = ev.currentTarget as HTMLElement;
    try {
      el.releasePointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    const y = this.sidebarDragTranslateY;
    this.sidebarPanPointerId = null;
    if (y >= this.sidebarPanDismissPx) {
      this.show_sidebar = false;
      this.sidebarDragTranslateY = 0;
    } else {
      requestAnimationFrame(() => (this.sidebarDragTranslateY = 0));
    }
  }

  showSidebar(toggler: any) {
    this.resetSidebarPanState();
    this.show_sidebar = true;
  }
  eligibleRoles() {
    this.roleService.eligibleRolesForRegistration(6, 5).subscribe({
      next: (req: HttpRes) => {
        if (req.code == 200 && req.data) {
          if (
            globalThis.window?.localStorage &&
            globalThis.window.sessionStorage !== undefined
          ) {
            const role_id = JSON.parse(
              this.encryptDecryptService.decryptUsingAES256(
                localStorage.getItem('role_id')
              )
            ) as number;
            const roles = req.data.data as EligibleRoleRow[];
            this.all_eligibleRole = roles.filter(
              (rl: EligibleRoleRow) => rl.role_id === role_id
            );
            sessionStorage.setItem(
              'data',
              JSON.stringify(this.all_eligibleRole)
            );
            this.commonService.setApiResponse(this.all_eligibleRole);
          }
        }
      },
      error: (error: unknown) => {
        console.error('Error creating role', error);
      },
      complete: () => undefined,
    });
  }
  eligibleRolesAccomodation() {
    this.roleService.eligibleRolesForRegistration(14, 5).subscribe({
      next: (req: HttpRes) => {
        if (req.code == 200 && req.data) {
          if (
            globalThis.window?.localStorage &&
            globalThis.window.sessionStorage !== undefined
          ) {
            const role_id = JSON.parse(
              this.encryptDecryptService.decryptUsingAES256(
                localStorage.getItem('role_id')
              )
            ) as number;
            const roles = req.data.data as EligibleRoleRow[];
            this.all_eligibleRole = roles.filter(
              (rl: EligibleRoleRow) => rl.role_id === role_id
            );
            sessionStorage.setItem(
              'accomodationRoles',
              JSON.stringify(this.all_eligibleRole)
            );
            this.commonService.setApiResponse(this.all_eligibleRole);
          }
        }
      },
      error: (error: unknown) => {
        console.error('Error creating role', error);
      },
      complete: () => undefined,
    });
  }

  isRegistrationTravelRoute() {
    const currentRoute = this.router.url;
    return currentRoute?.includes('registration/travel');
  }
  OpenDialog() {
    this.Open_dialog = true;
  }
  toggleTheme() {
    this.themeService.toggleTheme();
    this.Open_dialog = false;
  }

  show_tabs() {
    if (this.is_login) {
      return true;
    }
    return false;
  }

  // in case of logout show login sidebar
  navigate_to_login() {
    this.commonService.login_subject.next(true);
    this.commonService.is_login_success.next(false);
  }

  openHome() {
    if (this.tenant_name.toLowerCase() === 'ettu') {
      globalThis.window.location.href = 'https://www.ettu.org/';
    } else if (
      this.tenant_name.toLowerCase() === 'switz' ||
      this.tenant_name.toLowerCase() === 'preprod'
    ) {
      globalThis.window.location.href = 'https://www.swisstennis-padel.ch/de/';
    } else {
      this.router.navigate(['/home']);
    }
  }

  /** Opens request management (delegated to {@link UserMenuService}). */
  navigateToRequestManagement() {
    this.userMenuService.run('request_management');
  }

  /**
   * Optional hover prefetch — same target module as {@link navigateToMyRegistration}.
   * Does not change route or session state.
   */
  prefetchMyRegistrationModule(): void {
    if (!this.is_login) {
      return;
    }
    const prefetchUrl = this.isPlayer ? 'registration' : 'order-history';
    this.onTabHover(prefetchUrl);
  }

  /**
   * Opens the user's registrations (players) or order history (everyone else).
   *
   * Destination and eligible-role priming are delegated to
   * {@link UserMenuService}; the module preload is header-specific and stays.
   */
  navigateToMyRegistration() {
    this.dismissHeaderOverlays();
    this.preloadModuleForRoute(this.isPlayer ? 'registration' : 'order-history');
    this.userMenuService.run('my_registration');
  }

  /** Opens invoice history, then closes the header's own menus. */
  navigateToInvoiceHistory() {
    this.userMenuService.run('invoice_history');
    this.openDropDownMenu = '';
    this.show_sidebar = false;
  }

  navigateToUserMemberships() {
    this.router.navigate(['membership/my-memberships']);
    this.openDropDownMenu = '';
    this.show_sidebar = false;
  }

  isUserMembershipsRoute(): boolean {
    return this.router.url.includes('/membership/my-memberships');
  }

  isInvoiceHistoryRoute(): boolean {
    return this.router.url.includes('/order-history/invoice');
  }

  /** Hide Invoice History in profile for switz and test_switz tenants */
  get showInvoiceHistoryInProfile(): boolean {
    return (
      environment.tenant_Name !== 'switz' &&
      environment.tenant_Name !== 'test_switz'
    );
  }

  isPlayer: boolean = false;
  checkRole() {
    this.isPlayer = false;
    if (!globalThis.window?.localStorage) {
      return;
    }

    const token = localStorage.getItem('webToken') || localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        this.isPlayer = payload?.role_name?.toLowerCase() === 'player';
      } catch {
        /* ignore malformed JWT */
      }
    }
  }

  // Initialize Web Speech API
  initVoiceRecognition() {
    if (globalThis.window !== undefined) {
      const SpeechRecognition = (globalThis.window as any).SpeechRecognition || (globalThis.window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.warn('Speech recognition not supported in this browser');
        this.isVoiceSearchSupported = false;
        return;
      }

      try {
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
        this.recognition.maxAlternatives = 1;

        // Set language based on current UI language
        this.recognition.lang = this.getRecognitionLanguage();

        this.recognition.onstart = () => {
          this.isListening = true;

        };

        this.recognition.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;

          this.searchQuery = transcript;
          this.isSearchExpanded = true;
          this.syncMobileSearchBodyScrollLock();
          this.performSearch(transcript);
        };

        this.recognition.onerror = (event: any) => {
          console.error('Voice recognition error:', event.error);
          this.isListening = false;

          // Handle specific errors
          if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
            alert('Microphone access denied. Please allow microphone access in your browser settings.');
          } else if (event.error === 'no-speech') {
            alert('No speech detected. Please try again.');
          } else if (event.error === 'network') {
            alert('Network error. Please check your internet connection.');
          }
        };

        this.recognition.onend = () => {
          this.isListening = false;

        };

        this.isVoiceSearchSupported = true;

      } catch (error) {
        console.error('Error initializing voice recognition:', error);
        this.recognition = null;
        this.isVoiceSearchSupported = false;
      }
    }
  }

  // Helper method to get recognition language code
  private getRecognitionLanguage(): string {
    const langMap: { [key: string]: string } = {
      'en': 'en-US',
      'de': 'de-DE',
      'fr': 'fr-FR',
      'it': 'it-IT',
      'spain': 'es-ES',
      'norway': 'no-NO',
      'swedan': 'sv-SE',
      'sv': 'sl-SI',
      'cs': 'cs-CZ',
      'da': 'da-DK'
    };
    return langMap[this.currentLang] || 'en-US';
  }

  // Start voice search
  startVoiceSearch() {
    // Try to initialize recognition if it doesn't exist yet
    if (!this.recognition && globalThis.window !== undefined) {
      const SpeechRecognition = (globalThis.window as any).SpeechRecognition || (globalThis.window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        this.initVoiceRecognition();
      }
    }

    if (!this.recognition) {
      // Check if we're on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        alert('Voice search requires Chrome or Safari browser with microphone permissions enabled.');
      } else {
        alert('Voice search is not supported in your browser. Please use Chrome, Edge, or Safari.');
      }
      return;
    }

    if (this.isListening) {
      this.stopVoiceSearch();
      return;
    }

    try {
      // Expand search box first
      if (this.isSearchExpanded) {
        this.recognition.start();
      } else {
        this.isSearchExpanded = true;
        this.syncMobileSearchBodyScrollLock();
        setTimeout(() => {
          this.recognition.start();
        }, 100);
      }
    } catch (error: any) {
      console.error('Error starting voice recognition:', error);
      this.isListening = false;

      // Provide helpful error message
      if (error.message?.includes('already started')) {
        this.stopVoiceSearch();
        setTimeout(() => this.startVoiceSearch(), 100);
      } else {
        alert('Unable to start voice recognition. Please check your microphone permissions.');
      }
    }
  }

  // Stop voice search
  stopVoiceSearch() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }

  ngOnDestroy() {
    this.releaseMobileSearchBodyScrollLock();
    // Clean up voice recognition
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
